import { ExternalStyles } from 'bold-ui'
import { noop } from 'lodash'
import { useEffect, useRef, VideoHTMLAttributes } from 'react'
import { EventVideoChange } from 'view/videochamada/model-videochamada'

interface StreamPlayerProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'style'> {
  stream: MediaStream | null
  style?: ExternalStyles
  onVideoResize?(videoWidth: number, videoHeight: number): void
}

export function StreamPlayer(props: Readonly<StreamPlayerProps>) {
  const { stream, style, onVideoResize, muted = false, ...rest } = props
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const checkVideoOrientation = (e: EventVideoChange) => onVideoResize?.(e.target?.videoWidth, e.target?.videoHeight)
    const listener = checkVideoOrientation as EventListener
    video.addEventListener('loadedmetadata', listener)
    video.addEventListener('resize', listener)

    return () => {
      video.removeEventListener('loadedmetadata', listener)
      video.removeEventListener('resize', listener)
    }
  }, [onVideoResize])

  // Política de autoplay: sem gesto do usuário, o navegador bloqueia (NotAllowedError) o play de vídeo
  // com áudio. Fallback: toca mutado e restaura o áudio no primeiro gesto; listeners removidos via
  // AbortController no cleanup.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.srcObject = stream
    if (!stream) return

    const controller = new AbortController()

    // Restaura o áudio só se o play do gesto for liberado e o effect ainda estiver ativo
    // (sem abort, mesmo elemento); senão mantém mutado para o próximo gesto.
    const restoreAudioOnGesture = async () => {
      const currentVideo = videoRef.current
      if (controller.signal.aborted || !currentVideo) return
      try {
        await currentVideo.play()
      } catch {
        return
      }
      if (controller.signal.aborted || videoRef.current !== currentVideo) return
      currentVideo.muted = muted
      controller.abort()
    }
    const restoreAudio = () => {
      restoreAudioOnGesture().catch(noop)
    }

    const playOrFallback = async () => {
      try {
        await video.play()
        return
      } catch (error) {
        // não é bloqueio de autoplay: ignora
        if ((error as DOMException)?.name !== 'NotAllowedError') return
      }
      // bloqueio de autoplay: toca mutado e aguarda um gesto para restaurar o áudio
      if (controller.signal.aborted || muted || !videoRef.current) return
      videoRef.current.muted = true
      videoRef.current.play()?.catch(noop)
      document.addEventListener('pointerdown', restoreAudio, { signal: controller.signal })
      document.addEventListener('keydown', restoreAudio, { signal: controller.signal })
    }

    playOrFallback().catch(noop)

    return () => controller.abort()
  }, [stream, muted])

  return <video ref={videoRef} css={style} muted={muted} {...rest} />
}
